import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FaBolt,
  FaBoxOpen,
  FaCheck,
  FaCoins,
  FaPhoneAlt,
  FaShieldAlt,
  FaSnowflake,
  FaTools,
  FaWhatsapp,
} from "react-icons/fa";
import { useCart } from "../context/CartContext";
import { useKatalog, urunYolu } from "../data/katalog";
import { vitrinBul, vitrinIcerik } from "../data/vitrinUrunleri";

const WHATSAPP_NUMARA = "905377981129";
const TELEFON = "+905377981129";
const TELEFON_GORUNEN = "+90 537 798 11 29";

const IKONLAR = { ates: FaSnowflake, simsek: FaBolt, alet: FaTools, kova: FaBoxOpen, kalkan: FaShieldAlt, para: FaCoins };

const whatsappYolu = (metin) => `https://wa.me/${WHATSAPP_NUMARA}?text=${encodeURIComponent(metin)}`;

/** Arama motorları için: ürün + sık sorulan sorular */
const YapisalVeri = ({ icerik, gorsel }) => {
  useEffect(() => {
    const etiket = document.createElement("script");
    etiket.type = "application/ld+json";
    etiket.text = JSON.stringify([
      {
        "@context": "https://schema.org",
        "@type": "Product",
        name: icerik.ad,
        description: icerik.aciklama,
        image: new URL(gorsel, window.location.origin).href,
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: icerik.sss.map((s) => ({
          "@type": "Question",
          name: s.soru,
          acceptedAnswer: { "@type": "Answer", text: s.cevap },
        })),
      },
    ]);
    document.head.appendChild(etiket);
    return () => etiket.remove();
  }, [icerik, gorsel]);
  return null;
};

const Sayi = ({ etiket, deger, setDeger, birim }) => (
  <label className="block">
    <span className="text-[12px] font-semibold uppercase tracking-eyebrow text-muted">{etiket}</span>
    <span className="mt-1.5 flex items-center rounded-md border border-line bg-white focus-within:border-ink">
      <input
        type="number"
        inputMode="decimal"
        min="0"
        value={deger}
        onChange={(e) => setDeger(e.target.value)}
        className="h-11 w-full min-w-0 bg-transparent px-3 text-[15px] tabular-nums text-ink outline-none"
      />
      {birim && <span className="pe-3 text-[13px] text-muted">{birim}</span>}
    </span>
  </label>
);

/** Çukur ölçülerinden yaklaşık malzeme miktarı ve kova sayısı */
const MiktarHesabi = ({ vitrin, icerik }) => {
  const { t, i18n } = useTranslation();
  const ondalik = (n) => n.toLocaleString(i18n.language || "tr", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const [uzunluk, setUzunluk] = useState("60");
  const [genislik, setGenislik] = useState("40");
  const [derinlik, setDerinlik] = useState("5");
  const [adet, setAdet] = useState("1");

  const sayi = (s) => Math.max(0, Number(String(s).replace(",", ".")) || 0);
  const alan = (sayi(uzunluk) / 100) * (sayi(genislik) / 100) * sayi(adet); // m²
  const kg = alan * sayi(derinlik) * vitrin.kgM2Cm * 1.1; // %10 sıkıştırma ve fire payı
  const kova = kg > 0 ? Math.ceil(kg / vitrin.kovaKg - 1e-9) : 0;
  const kgYuvarlak = Math.ceil(kg);

  const mesaj = `${icerik.whatsapp}\n${t("spotlight.whatsappCalc", {
    kg: kgYuvarlak,
    adet: kova,
    alan: ondalik(alan),
    derinlik: sayi(derinlik),
  })}`;

  return (
    <div className="grid gap-8 rounded-2xl border border-line bg-white p-6 sm:p-8 lg:grid-cols-[1.2fr_1fr] lg:gap-12">
      <div>
        <div className="grid grid-cols-2 gap-4">
          <Sayi etiket={t("spotlight.length")} deger={uzunluk} setDeger={setUzunluk} birim="cm" />
          <Sayi etiket={t("spotlight.width")} deger={genislik} setDeger={setGenislik} birim="cm" />
          <Sayi etiket={t("spotlight.depth")} deger={derinlik} setDeger={setDerinlik} birim="cm" />
          <Sayi etiket={t("spotlight.count")} deger={adet} setDeger={setAdet} />
        </div>
        <p className="mt-4 text-[12.5px] leading-relaxed text-muted">{t("spotlight.calcNote", { kova: vitrin.kovaKg })}</p>
      </div>

      <div className="flex flex-col justify-center rounded-xl bg-ink p-6 text-white">
        <p className="text-[12px] font-semibold uppercase tracking-eyebrow text-white/60">{t("spotlight.youNeed")}</p>
        <p className="mt-2 text-[44px] font-bold leading-none tracking-tight tabular-nums">
          {kova} <span className="text-[20px] font-semibold text-white/80">{t("spotlight.bucketUnit", { count: kova })}</span>
        </p>
        <p className="mt-2 text-[14px] text-white/70">{t("spotlight.approxKg", { kg: kgYuvarlak, alan: ondalik(alan) })}</p>
        <a
          href={whatsappYolu(mesaj)}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-6 inline-flex items-center justify-center gap-2 rounded-md bg-[#25D366] px-5 py-3 text-[14px] font-semibold text-white transition-colors hover:bg-[#1ebe5b]"
        >
          <FaWhatsapp size={17} aria-hidden="true" />
          {t("spotlight.askWithCalc")}
        </a>
      </div>
    </div>
  );
};

export default function VitrinSayfasi() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const dil = (i18n.language || "tr").split("-")[0];
  const vitrin = vitrinBul(slug);
  const { urunler } = useKatalog();
  const { ekle } = useCart();
  const [eklendi, setEklendi] = useState(false);

  const icerik = vitrin ? vitrinIcerik(vitrin, dil) : null;
  const urun = useMemo(() => (vitrin ? urunler.find((u) => u.id === vitrin.urunId) : null), [urunler, vitrin]);

  useEffect(() => {
    if (!icerik) return;
    const onceki = document.title;
    document.title = `${icerik.ad} — ${t("siteName")}`;
    const meta = document.querySelector('meta[name="description"]');
    const oncekiAciklama = meta?.getAttribute("content");
    meta?.setAttribute("content", `${icerik.slogan} ${icerik.aciklama}`);
    return () => {
      document.title = onceki;
      if (meta && oncekiAciklama != null) meta.setAttribute("content", oncekiAciklama);
    };
  }, [icerik, t]);

  if (!vitrin) return <Navigate to="/urunler" replace />;

  const sepeteEkle = () => {
    if (!urun) return;
    ekle(urun, "");
    setEklendi(true);
    setTimeout(() => setEklendi(false), 1600);
  };

  return (
    <main className="pb-20 sm:pb-0">
      <YapisalVeri icerik={icerik} gorsel={vitrin.gorsel} />

      {/* ---------------- Giriş ---------------- */}
      <section className="relative overflow-hidden bg-ink text-white">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-60"
          style={{
            background:
              "radial-gradient(700px 500px at 85% 20%, rgba(194,65,12,.45), transparent 60%), radial-gradient(600px 500px at 0% 100%, rgba(22,116,174,.35), transparent 60%)",
          }}
        />
        <div className="container-page relative grid items-center gap-10 py-14 sm:py-20 lg:grid-cols-[1.05fr_1fr] lg:gap-16">
          <div className="text-start">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3.5 py-1.5 text-[12px] font-semibold uppercase tracking-wide">
              <span className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_0_4px_rgba(52,211,153,.25)]" />
              {t("spotlight.newInStore")}
            </span>
            <h1 className="mt-6 text-[38px] font-bold text-white leading-[1.05] tracking-[-0.03em] sm:text-[54px]">{icerik.ad}</h1>
            <p className="mt-4 text-[20px] font-semibold leading-snug text-orange-300 sm:text-[24px]">{icerik.slogan}</p>
            <p className="mt-5 max-w-xl text-[15.5px] leading-[1.75] text-white/75">{icerik.aciklama}</p>

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={whatsappYolu(icerik.whatsapp)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-md bg-[#25D366] px-5 py-3 text-[14.5px] font-semibold text-white transition-colors hover:bg-[#1ebe5b]"
              >
                <FaWhatsapp size={18} aria-hidden="true" />
                {t("spotlight.whatsapp")}
              </a>
              <a
                href={`tel:${TELEFON}`}
                className="inline-flex items-center gap-2 rounded-md border border-white/25 px-5 py-3 text-[14.5px] font-semibold text-white transition-colors hover:bg-white hover:text-ink"
              >
                <FaPhoneAlt size={13} aria-hidden="true" />
                {t("spotlight.call")}
              </a>
              {urun && (
                <button
                  type="button"
                  onClick={sepeteEkle}
                  className="inline-flex items-center gap-2 rounded-md border border-white/25 px-5 py-3 text-[14.5px] font-semibold text-white transition-colors hover:bg-white hover:text-ink"
                >
                  {eklendi ? <FaCheck size={13} aria-hidden="true" /> : null}
                  {eklendi ? t("shop.added") : t("shop.addToCart")}
                </button>
              )}
            </div>

            <ul className="mt-9 flex flex-wrap gap-x-6 gap-y-2 text-[13.5px] text-white/80">
              {icerik.avantajlar.slice(0, 3).map((a) => (
                <li key={a.baslik} className="flex items-center gap-2">
                  <FaCheck className="text-emerald-400" size={12} aria-hidden="true" />
                  {a.baslik}
                </li>
              ))}
            </ul>
          </div>

          <figure className="relative mx-auto w-full max-w-[520px] lg:max-w-none">
            <div className="overflow-hidden rounded-2xl bg-[#e9e8e4] shadow-[0_40px_80px_-30px_rgba(0,0,0,.7)]">
              <img src={vitrin.gorsel} alt={icerik.ad} className="aspect-square w-full object-cover" />
            </div>
            {vitrin.temsiliGorsel && (
              <figcaption className="mt-2 text-end text-[11.5px] text-white/45">{t("spotlight.illustrative")}</figcaption>
            )}
          </figure>
        </div>
      </section>

      {/* ---------------- Avantajlar ---------------- */}
      <section className="container-page py-16 sm:py-20">
        <p className="eyebrow">{t("spotlight.whyEyebrow")}</p>
        <h2 className="mt-3 max-w-2xl text-[28px] font-semibold leading-tight tracking-tight text-ink sm:text-[34px]">
          {t("spotlight.why", { ad: icerik.ad })}
        </h2>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {icerik.avantajlar.map((a) => {
            const Ikon = IKONLAR[a.ikon] || FaCheck;
            return (
              <div key={a.baslik} className="rounded-xl border border-line bg-white p-6 text-start">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent-soft text-accent">
                  <Ikon size={20} aria-hidden="true" />
                </span>
                <h3 className="mt-5 text-[16.5px] font-semibold text-ink">{a.baslik}</h3>
                <p className="mt-2 text-[14px] leading-relaxed text-body">{a.metin}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ---------------- Kullanım alanları ---------------- */}
      <section className="border-y border-line bg-canvas">
        <div className="container-page py-14 sm:py-16">
          <h2 className="text-[24px] font-semibold tracking-tight text-ink sm:text-[28px]">{t("spotlight.where")}</h2>
          <ul className="mt-7 flex flex-wrap gap-2.5">
            {icerik.alanlar.map((a) => (
              <li key={a} className="rounded-full border border-line bg-white px-4 py-2 text-[14px] text-ink">
                {a}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ---------------- Uygulama ---------------- */}
      <section className="container-page py-16 sm:py-20">
        <p className="eyebrow">{t("spotlight.howEyebrow")}</p>
        <h2 className="mt-3 text-[28px] font-semibold leading-tight tracking-tight text-ink sm:text-[34px]">{t("spotlight.how")}</h2>
        <ol className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {icerik.adimlar.map((a, i) => (
            <li key={a.baslik} className="relative rounded-xl border border-line bg-white p-6 text-start">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-ink text-[16px] font-bold text-white">{i + 1}</span>
              <h3 className="mt-5 text-[16.5px] font-semibold text-ink">{a.baslik}</h3>
              <p className="mt-2 text-[14px] leading-relaxed text-body">{a.metin}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* ---------------- Miktar hesabı ---------------- */}
      <section id="hesapla" className="scroll-mt-32 border-y border-line bg-canvas">
        <div className="container-page py-16 sm:py-20">
          <p className="eyebrow">{t("spotlight.calcEyebrow")}</p>
          <h2 className="mt-3 text-[28px] font-semibold leading-tight tracking-tight text-ink sm:text-[34px]">{t("spotlight.calc")}</h2>
          <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-body">{t("spotlight.calcText")}</p>
          <div className="mt-9">
            <MiktarHesabi vitrin={vitrin} icerik={icerik} />
          </div>
        </div>
      </section>

      {/* ---------------- Sık sorulanlar ---------------- */}
      <section className="container-page py-16 sm:py-20">
        <h2 className="text-[28px] font-semibold tracking-tight text-ink sm:text-[34px]">{t("spotlight.faq")}</h2>
        <div className="mt-8 divide-y divide-line rounded-xl border border-line bg-white">
          {icerik.sss.map((s, i) => (
            <details key={s.soru} open={i === 0} className="group px-5 py-4 sm:px-6">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-start text-[15.5px] font-semibold text-ink">
                {s.soru}
                <span aria-hidden="true" className="text-[20px] font-normal text-muted transition-transform group-open:rotate-45">+</span>
              </summary>
              <p className="mt-3 text-[14.5px] leading-relaxed text-body">{s.cevap}</p>
            </details>
          ))}
        </div>
      </section>

      {/* ---------------- Toptan ---------------- */}
      <section className="container-page pb-20">
        <div className="flex flex-col items-start gap-6 rounded-2xl bg-accent-soft p-8 sm:flex-row sm:items-center sm:justify-between sm:p-10">
          <div className="text-start">
            <h2 className="text-[22px] font-semibold tracking-tight text-ink sm:text-[26px]">{t("spotlight.wholesaleTitle")}</h2>
            <p className="mt-2 max-w-xl text-[14.5px] leading-relaxed text-body">{t("spotlight.wholesaleText")}</p>
          </div>
          <div className="flex shrink-0 flex-wrap gap-3">
            <Link to="/toptan-teklif" className="btn-primary">
              {t("spotlight.wholesaleCta")}
            </Link>
            {urun && (
              <Link to={urunYolu(urun)} className="btn-secondary">
                {t("spotlight.productPage")}
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* ---------------- Telefonda sabit alt çubuk ---------------- */}
      <div className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-2 gap-2 border-t border-line bg-white/95 p-3 backdrop-blur sm:hidden">
        <a
          href={whatsappYolu(icerik.whatsapp)}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 rounded-md bg-[#25D366] py-3 text-[14px] font-semibold text-white"
        >
          <FaWhatsapp size={17} aria-hidden="true" />
          {t("spotlight.whatsappShort")}
        </a>
        <a href={`tel:${TELEFON}`} className="flex items-center justify-center gap-2 rounded-md bg-ink py-3 text-[14px] font-semibold text-white">
          <FaPhoneAlt size={13} aria-hidden="true" />
          <span dir="ltr">{TELEFON_GORUNEN}</span>
        </a>
      </div>
    </main>
  );
}
